import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, writeBatch } from "firebase/firestore";
import { db } from "./firebase";
import { serviceToFirestore } from "../models/service";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "../services/collections";

/**
 * ServiceService
 * Centraliza as operações de banco de dados para o módulo de Serviços e Produtos.
 * Coleção: tenants/{idTenant}/services
 */
const ServiceService = {
    getServices: async (idTenant) => {
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.SERVICES),
                orderBy("order", "asc")
            );
            const querySnapshot = await getDocs(q);
            const items = [];
            querySnapshot.forEach((docSnap) => {
                items.push({ id: docSnap.id, ...docSnap.data() });
            });
            return items;
        } catch (error) {
            console.error("Erro ao listar serviços e produtos:", error);
            throw error;
        }
    },

    save: async (formData, idTenant, authUser, existingItems = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const firestoreData = serviceToFirestore(formData, idTenant, authUser);

        try {
            if (formData.id) {
                // UPDATE
                const docRef = doc(db, "tenants", idTenant, COLLECTIONS.SERVICES, formData.id);
                const oldItem = existingItems.find(c => c.id === formData.id) || {};
                await updateDoc(docRef, firestoreData);

                // Auditoria
                const changes = AuditService.calculateDiff(oldItem, firestoreData);
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.SERVICES,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: formData.id,
                    entityName: firestoreData.nameService,
                    description: `Atualizou o serviço/produto "${firestoreData.nameService}"`,
                    details: changes
                });

                return { id: formData.id, ...firestoreData };
            } else {
                // CREATE — define order como último
                if (!formData.order && existingItems.length > 0) {
                    firestoreData.order = existingItems.length;
                }

                const newDocRef = await addDoc(collection(db, "tenants", idTenant, COLLECTIONS.SERVICES), firestoreData);

                // Auditoria
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.SERVICES,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: newDocRef.id,
                    entityName: firestoreData.nameService,
                    description: `Criou o serviço/produto "${firestoreData.nameService}"`
                });

                return { id: newDocRef.id, ...firestoreData };
            }
        } catch (error) {
            console.error("Erro no ServiceService.save:", error);
            throw error;
        }
    },

    delete: async (itemId, idTenant, authUser, itemName) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTIONS.SERVICES, itemId));

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.SERVICES,
                action: AUDIT_ACTIONS.DELETE,
                entityId: itemId,
                entityName: itemName,
                description: `Excluiu o serviço/produto "${itemName || itemId}"`
            });

            return true;
        } catch (error) {
            console.error("Erro no ServiceService.delete:", error);
            throw error;
        }
    },

    reorder: async (items, idTenant, authUser) => {
        if (!idTenant || !items.length) return;

        try {
            const batch = writeBatch(db);
            items.forEach((item, index) => {
                const docRef = doc(db, "tenants", idTenant, COLLECTIONS.SERVICES, item.id);
                batch.update(docRef, { order: index });
            });

            await batch.commit();

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.SERVICES,
                action: AUDIT_ACTIONS.UPDATE,
                description: `Reordenou a lista de serviços e produtos (${items.length} itens)`
            });

            return true;
        } catch (error) {
            console.error("Erro no ServiceService.reorder:", error);
            throw error;
        }
    }
};

export default ServiceService;
